/**
 * Minimal, safe CSV writer for exporting data that includes text typed by
 * strangers (e.g. the search log).
 *
 * Three things matter beyond commas and quotes:
 *  - Formula injection: a cell that starts with = + - @ (or a tab / CR) is
 *    executed as a formula by Excel and Google Sheets when the file is
 *    opened. A search like `=HYPERLINK("http://evil","click")` would then
 *    run on whoever reviews the export. Such cells get a leading single
 *    quote, the standard neutralisation, which spreadsheets display as
 *    plain text.
 *  - Line breaks inside a cell are kept, quoted, so a multi-line entry
 *    can't spill into extra rows.
 *  - A UTF-8 byte-order mark is prepended: without it Excel guesses a
 *    legacy encoding and garbles non-English text (Hindi, Japanese,
 *    accented Spanish...), which is exactly what a multilingual search
 *    log will contain.
 */
export type CsvValue = string | number | boolean | null | undefined;

const FORMULA_START = /^[=+\-@\t\r]/;

export function csvCell(value: CsvValue): string {
  if (value === null || value === undefined) return "";
  let s = typeof value === "string" ? value : String(value);
  // Numbers and booleans are trusted; only strings can carry a payload.
  // (A negative number like -5 must stay a number, not become "'-5".)
  if (typeof value === "string" && FORMULA_START.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv<T extends Record<string, CsvValue>>(
  rows: T[],
  columns: { key: keyof T; header: string }[],
): string {
  const head = columns.map((c) => csvCell(c.header)).join(",");
  const body = rows.map((r) => columns.map((c) => csvCell(r[c.key])).join(","));
  return "\uFEFF" + [head, ...body].join("\r\n") + "\r\n";
}

/** Triggers a browser download of `content` as a file. */
export function downloadTextFile(filename: string, content: string, mime = "text/csv;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
